import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box, Layer, Text } from 'grommet';
import { FormPrevious, FormNext, FormClose } from 'grommet-icons';
import SignatureCanvas from 'react-signature-canvas';
import { useAppTheme } from '@Components/Context';
import {
  AppAnchor, AppButton, RadioButtonSlider, SubmissionLoader, Spinning,
} from '@Components/Control';
import {
  CidOnboardingTermsModal, TermsOfUseModal, PrivacyPolicyModal,
} from '@Components/Modal';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import {
  fetchPlanManagement,
  clearPlanManagement,
  clearPlanUpgradeResponse,
  initiateUpgradeSocialPlanRequest,
} from '@Actions';
import { paths } from '@Components/configs';
import { StyledPlatformTag } from '../../Partial/Onboarding/StyledOnboardingComponents';
import { SectionContainer } from './AccountPage';
import { StyledStatusBadge, StyledText } from './StyledAccountPage';

const HANDWRITTEN_FONTS = ['Segoe Script', 'Bradley Hand', 'Brush Script MT', 'cursive', 'sans-serif'];
const SIG_WIDTH = 500;
const SIG_HEIGHT = 260;

const SOCIAL_PLATFORM_OPTIONS = [
  { id: 'tiktok', label: 'TikTok' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'dailymotion', label: 'Dailymotion' },
  { id: 'telegram', label: 'Telegram' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'twitter', label: 'X' },
  { id: 'pinterest', label: 'Pinterest' },
  { id: 'reddit', label: 'Reddit' },
  { id: 'soundcloud', label: 'SoundCloud' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'blogspot', label: 'Blogspot' },
  { id: 'quora', label: 'Quora' },
  { id: 'tumblr', label: 'Tumblr' },
  { id: 'disqus', label: 'Disqus' },
  { id: 'vk', label: 'VK' },
];

const platformLabel = (id) => {
  const option = SOCIAL_PLATFORM_OPTIONS.find((p) => p.id === id);
  return option ? option.label : id;
};

const formatCurrency = (amount) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format(amount);

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
};

const addOneDay = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
};

const BackLink = ({ altHighlight }) => (
  <Box pad={{ bottom: '2rem' }}>
    <AppAnchor path={paths.account}>
      <Box direction="row" align="center" gap="0.15rem">
        <FormPrevious size="0.85rem" color={altHighlight} />
        <Text size="0.85rem" color={altHighlight}>Back to account</Text>
      </Box>
    </AppAnchor>
  </Box>
);

BackLink.propTypes = {
  altHighlight: PropTypes.string.isRequired,
};

const PlanUpgradePage = ({
  small,
  location,
  planManagementData,
  planManagementLoading: planLoading,
  planUpgradeResponse,
  submitLoading,
  fetchPlan,
  clearPlan,
  clearUpgradeResponse,
  upgradeSocialPlan,
}) => {
  const { theme: { page, button }, isDarkMode } = useAppTheme();
  const [selectedPlatforms, setSelectedPlatforms] = React.useState([]);

  // Signature modal state
  const [showSignModal, setShowSignModal] = React.useState(false);
  const [useNewSignature, setUseNewSignature] = React.useState(false);
  const [sigMode, setSigMode] = React.useState('draw');
  const [drawSignature, setDrawSignature] = React.useState('');
  const [typedSignature, setTypedSignature] = React.useState('');
  const [mediaSignature, setMediaSignature] = React.useState('');
  const [fileInputKey, setFileInputKey] = React.useState(0);
  const [showAgreement, setShowAgreement] = React.useState(false);
  const [showTerms, setShowTerms] = React.useState(false);
  const [showPrivacy, setShowPrivacy] = React.useState(false);
  const sigPadRef = React.useRef(null);
  const sigContainerRef = React.useRef(null);
  const [canvasWidth, setCanvasWidth] = React.useState(SIG_WIDTH);

  const existingSignature = planManagementData?.existingSignature || null;

  const getSignatureBase64 = () => {
    if (sigMode === 'draw') return drawSignature;
    if (sigMode === 'type') {
      if (!typedSignature) return '';
      const canvas = document.createElement('canvas');
      canvas.width = SIG_WIDTH;
      canvas.height = SIG_HEIGHT - 20;
      const ctx = canvas.getContext('2d');
      ctx.font = `36px ${HANDWRITTEN_FONTS.join(',')}`;
      ctx.fillStyle = '#000000';
      ctx.textBaseline = 'middle';
      ctx.fillText(typedSignature, 20, SIG_HEIGHT / 2 - 10);
      return canvas.toDataURL();
    }
    if (sigMode === 'upload') return mediaSignature;
    return '';
  };

  const handleDrawEnd = () => {
    if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
      setDrawSignature(sigPadRef.current.getTrimmedCanvas().toDataURL());
    } else {
      setDrawSignature('');
    }
  };

  const handleResetSignature = () => {
    setDrawSignature('');
    setTypedSignature('');
    setMediaSignature('');
    setFileInputKey((k) => k + 1);
    if (sigPadRef.current) sigPadRef.current.clear();
  };

  const handleCloseSignModal = () => {
    setShowSignModal(false);
    handleResetSignature();
    setSigMode('draw');
    setUseNewSignature(false);
  };

  React.useEffect(() => {
    fetchPlan();
    return () => {
      clearPlan();
      clearUpgradeResponse();
    };
  }, []);

  React.useEffect(() => {
    if (planManagementData?.socialPlan?.platforms) {
      setSelectedPlatforms(planManagementData.socialPlan.platforms);
    }
  }, [planManagementData]);

  React.useEffect(() => {
    const measure = () => {
      if (sigContainerRef.current) {
        setCanvasWidth(sigContainerRef.current.offsetWidth);
      }
    };
    if (showSignModal) {
      measure();
      window.addEventListener('resize', measure);
    }
    return () => window.removeEventListener('resize', measure);
  }, [showSignModal, useNewSignature, sigMode]);

  const usePageBg = isDarkMode ? page.background : page.altBg;
  const useSectionBg = isDarkMode ? page.altBg : page.background;

  const availableActions = planManagementData?.availableActions || {};
  const canAddSocial = Boolean(availableActions.canAddSocial);
  const canExpandSocial = Boolean(availableActions.canExpandSocial);
  const canUpgrade = canAddSocial || canExpandSocial;

  const currentPlatforms = planManagementData?.socialPlan?.platforms || [];
  const perPlatformFee = planManagementData?.socialPlan?.perPlatformMonthlyFeeAmount ?? 0;
  const cidMonthlyFee = planManagementData?.cidPlan?.cidMonthlyFeeAmount ?? 0;
  const cidPlan = planManagementData?.cidPlan;
  const billing = planManagementData?.billing;

  // Manually onboarded users have no onboarding document link — plan upgrades
  // are not self-service for them (per backend lead decision).
  const isManuallyOnboarded = planManagementData && !planManagementData.onboardingId;
  const isPaymentPending = Boolean(billing?.paymentPending);

  const channelCount = cidPlan?.channel_count ?? 1;
  const newPlatforms = selectedPlatforms.filter((p) => !currentPlatforms.includes(p));
  const totalPlatforms = selectedPlatforms.length;
  const newMonthlyTotal = totalPlatforms * perPlatformFee * channelCount;

  const togglePlatform = (platformId) => {
    if (currentPlatforms.includes(platformId)) return;
    setSelectedPlatforms((prev) => (
      prev.includes(platformId)
        ? prev.filter((p) => p !== platformId)
        : [...prev, platformId]
    ));
  };

  const handleSubmit = () => {
    setShowSignModal(true);
  };

  const handleSignAndSubmit = () => {
    const sig = (!useNewSignature && existingSignature) ? existingSignature : getSignatureBase64();
    if (!sig) return;
    upgradeSocialPlan(selectedPlatforms, sig);
    handleCloseSignModal();
  };

  const isInitialLoading = planLoading && !planManagementData;

  if (isInitialLoading) {
    return (
      <AuthNavWrapper
        small={small}
        location={location}
        bannerProps={{ title: 'Account information', subTitle: 'Manage plan' }}
      >
        <Box flex align="center" justify="center" background={usePageBg} pad="2rem">
          <Spinning size="large" color={page.highlight} />
        </Box>
      </AuthNavWrapper>
    );
  }

  // Post-upgrade confirmation screen
  if (planUpgradeResponse) {
    const effectiveAt = formatDate(addOneDay(planUpgradeResponse.effectiveAt));
    const billingEffectiveAt = formatDate(planUpgradeResponse.billingEffectiveAt);
    const { contractUrl } = planUpgradeResponse;
    const timingMode = planUpgradeResponse.billingTimingMode;

    return (
      <AuthNavWrapper
        small={small}
        location={location}
        bannerProps={{ title: 'Account information', subTitle: 'Plan upgraded' }}
      >
        <Box
          flex
          direction="column"
          background={usePageBg}
          pad={{ vertical: '2rem', horizontal: small ? '1rem' : '1.5rem' }}
        >
          <BackLink altHighlight={page.altHighlight} />
          <SectionContainer
            small={small}
            sectionTitle="Plan upgrade confirmed"
            customWidth={small ? '100%' : '40%'}
            textColor={page.primaryText}
            sectionBg={useSectionBg}
          >
            <Box direction="column" gap="1.25rem">
              <StyledStatusBadge background="#EDFDF5">
                <Text size="1rem" color="var(--app-success-dark)" weight={600}>
                  Your plan has been upgraded successfully
                </Text>
              </StyledStatusBadge>
              <StyledStatusBadge background="#FEF9EC">
                <Text size="0.9rem" color="#92690A">
                  Your previous contract is now null and void. This updated contract
                  supersedes all prior agreements.
                </Text>
              </StyledStatusBadge>
              <StyledText size="1rem" color={page.primaryText}>
                {'Your updated social monitoring plan is now active.'}
                {effectiveAt && (
                  <>
                    {' '}
                    {'Platform monitoring will begin on '}
                    <strong>{effectiveAt}</strong>
                    .
                  </>
                )}
              </StyledText>
              {timingMode === 'next_cycle_deferred' && billingEffectiveAt && (
                <StyledText size="1rem" color={page.primaryText}>
                  {'Billing for the new platforms will start on '}
                  <strong>{billingEffectiveAt}</strong>
                  .
                </StyledText>
              )}
              {contractUrl && (
                <Box direction="row" gap="0.5rem" align="center">
                  <Text size="1rem" color={page.primaryText} weight={500}>Contract:</Text>
                  <a
                    href={contractUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: page.altHighlight, textDecoration: 'underline', fontWeight: 600 }}
                  >
                    View updated contract
                  </a>
                </Box>
              )}
              <AppAnchor path={paths.account}>
                <AppButton
                  overrideHover
                  level="authSubmit"
                  bgColor={button.primaryBg}
                  color="white"
                  label="Return to account"
                  alignSelf="start"
                />
              </AppAnchor>
            </Box>
          </SectionContainer>
        </Box>
      </AuthNavWrapper>
    );
  }

  const renderUpgradeSection = () => {
    if (isManuallyOnboarded) {
      return (
        <SectionContainer
          small={small}
          sectionTitle="Plan upgrades"
          customWidth={small ? '100%' : '45%'}
          textColor={page.primaryText}
          sectionBg={useSectionBg}
        >
          <StyledText size="1rem" color={page.primaryText}>
            {'Plan management is not self-service for your account. To make any changes, please reach out to our team at '}
            <a href="mailto:contact@redflagai.co" style={{ color: page.altHighlight }}>
              contact@redflagai.co
            </a>
            .
          </StyledText>
        </SectionContainer>
      );
    }

    if (canUpgrade) {
      return (
        <SectionContainer
          small={small}
          sectionTitle={canAddSocial ? 'Add social platform monitoring' : 'Add platforms'}
          customWidth={small ? '100%' : '45%'}
          textColor={page.primaryText}
          sectionBg={useSectionBg}
        >
          <Box direction="column" gap="1.25rem">
            {isPaymentPending && (
              <StyledStatusBadge background="#FCF8EF">
                <Text size="1rem" color="#AB5741" weight={600}>
                  Your initial payment is pending. Plan upgrades will be available once
                  your payment is completed.
                  {billing?.paymentLink && (
                    <>
                      {' '}
                      <a
                        href={billing.paymentLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#AB5741', textDecoration: 'underline' }}
                      >
                        Complete payment
                      </a>
                    </>
                  )}
                </Text>
              </StyledStatusBadge>
            )}
            {canAddSocial && (
              <StyledText size="1rem" color={page.primaryText}>
                Add social platform monitoring to your plan. Select the platforms
                you want us to scan for unauthorized content.
              </StyledText>
            )}
            {canExpandSocial && (
              <StyledText size="1rem" color={page.primaryText}>
                Expand your social monitoring by adding more platforms.
                Currently monitored platforms are already active and cannot be removed here.
              </StyledText>
            )}

            {/* Platform selection — already-active ones shown inert, new ones selectable */}
            {canExpandSocial && currentPlatforms.length > 0 && (
              <Box direction="column" gap="0.35rem">
                <Text size="0.85rem" color={page.primaryText} weight={500}>
                  Currently monitoring:
                </Text>
                <Box direction="row" wrap>
                  {currentPlatforms.map((id) => (
                    <StyledPlatformTag
                      key={id}
                      active
                      highlightColor={page.highlight}
                      hoverBg={page.highlight}
                      pad={{ vertical: '0.4rem', horizontal: '0.75rem' }}
                      round="var(--app-radius)"
                      style={{ cursor: 'default', opacity: 0.6 }}
                    >
                      <Text size="0.95rem" weight={600} color="white">
                        {platformLabel(id)}
                      </Text>
                    </StyledPlatformTag>
                  ))}
                </Box>
              </Box>
            )}

            <Box direction="column" gap="0.35rem">
              {canExpandSocial && (
                <Text size="0.85rem" color={page.primaryText} weight={500}>
                  Select platforms to add:
                </Text>
              )}
              <Box direction="row" wrap>
                {SOCIAL_PLATFORM_OPTIONS
                  .filter(({ id }) => !currentPlatforms.includes(id) && !(cidPlan && id === 'youtube'))
                  .map(({ id, label }) => {
                    const isSelected = selectedPlatforms.includes(id);
                    return (
                      <StyledPlatformTag
                        key={id}
                        active={isSelected}
                        highlightColor={button.primaryBg}
                        hoverBg={button.selectBg}
                        pad={{ vertical: '0.4rem', horizontal: '0.75rem' }}
                        round="var(--app-radius)"
                        onClick={() => togglePlatform(id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') togglePlatform(id);
                        }}
                        tabIndex={0}
                      >
                        <Text
                          size="0.95rem"
                          weight={600}
                          color={isSelected ? 'white' : page.primaryText}
                        >
                          {label}
                        </Text>
                      </StyledPlatformTag>
                    );
                  })}
              </Box>
            </Box>

            {/* Pricing summary shown once new platforms are selected */}
            {newPlatforms.length > 0 && (
              <Box
                direction="column"
                gap="0.5rem"
                pad={{ vertical: '0.75rem', horizontal: '1rem' }}
                background={isDarkMode ? page.background : '#F9FAFB'}
                round="small"
              >
                <Box direction="row" gap="0.5rem" align="center" wrap>
                  <Text size="0.9rem" color={page.primaryText} weight={400}>Adding:</Text>
                  <Text size="0.9rem" color={page.primaryText} weight={500}>
                    {newPlatforms.map(platformLabel).join(', ')}
                  </Text>
                </Box>
                {perPlatformFee > 0 && (
                  <>
                    <Box direction="row" gap="0.5rem" align="center" wrap>
                      <Text size="0.9rem" color={page.primaryText} weight={400}>
                        Per platform / month:
                      </Text>
                      <Text size="0.9rem" color={page.primaryText} weight={500}>
                        {formatCurrency(perPlatformFee)}
                      </Text>
                    </Box>
                    <Box direction="row" gap="0.5rem" align="center" wrap>
                      <Text size="0.9rem" color={page.primaryText} weight={400}>
                        {`Social Scanning total (${totalPlatforms} platform${totalPlatforms !== 1 ? 's' : ''}) / month:`}
                      </Text>
                      <Text size="0.9rem" color={page.primaryText} weight={500}>
                        {formatCurrency(newMonthlyTotal)}
                      </Text>
                    </Box>
                    {cidMonthlyFee > 0 && (
                      <Box direction="row" gap="0.5rem" align="center" wrap>
                        <Text size="0.9rem" color={page.primaryText} weight={400}>
                          Complete total for the service / month:
                        </Text>
                        <Text size="0.9rem" color={page.primaryText} weight={600}>
                          {formatCurrency(newMonthlyTotal + cidMonthlyFee)}
                        </Text>
                      </Box>
                    )}
                  </>
                )}
                {billing?.billingTimingMode === 'next_cycle_deferred' && billing?.billingEffectiveAt && (
                  <Box direction="row" gap="0.5rem" align="center" wrap>
                    <Text size="0.9rem" color={page.primaryText} weight={400}>
                      Billing starts:
                    </Text>
                    <Text size="0.9rem" color={page.primaryText} weight={500}>
                      {formatDate(billing.billingEffectiveAt)}
                    </Text>
                  </Box>
                )}
              </Box>
            )}

            <Box>
              <AppButton
                overrideHover
                onClick={handleSubmit}
                level="authSubmit"
                disabled={newPlatforms.length === 0 || submitLoading || isPaymentPending}
                bgColor={button.primaryBg}
                color="white"
                label="Continue"
              />
            </Box>
          </Box>
        </SectionContainer>
      );
    }

    if (planManagementData) {
      return (
        <SectionContainer
          small={small}
          sectionTitle="Plan upgrades"
          customWidth={small ? '100%' : '45%'}
          textColor={page.primaryText}
          sectionBg={useSectionBg}
        >
          <StyledText size="1rem" color={page.primaryText}>
            {'No plan upgrade options are currently available. If you need to modify your plan, please contact us at '}
            <a href="mailto:contact@redflagai.co" style={{ color: page.altHighlight }}>
              contact@redflagai.co
            </a>
            .
          </StyledText>
        </SectionContainer>
      );
    }

    return null;
  };

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{ title: 'Account information', subTitle: 'Manage plan' }}
    >
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '2rem', horizontal: small ? '1rem' : '1.5rem' }}
      >
        {submitLoading && (
          <SubmissionLoader
            textColor={page.primaryText}
            loaderColor={page.highlight}
            message="Upgrading plan..."
            contentWidth="18rem"
          />
        )}
        <BackLink altHighlight={page.altHighlight} />
        {/* Current plan overview */}
        <SectionContainer
          small={small}
          sectionTitle="Current plan"
          customWidth={small ? '100%' : '45%'}
          textColor={page.primaryText}
          sectionBg={useSectionBg}
          margin={{ bottom: '2rem' }}
        >
          <Box direction="column" gap="1.25rem">
            {cidPlan && (
              <Box direction="column" gap="0.5rem">
                <Box direction="row" align="center" gap="1rem">
                  <Text size="1.1rem" color={page.primaryText} weight={600}>
                    YouTube Content ID
                  </Text>
                  <StyledStatusBadge background="#EDFDF5">
                    <Text size="0.9rem" color="var(--app-success-dark)" weight={600}>Active</Text>
                  </StyledStatusBadge>
                </Box>
                <Text size="0.9rem" color={page.primaryText}>
                  {'CID plan modifications are managed by the Redflag team. Contact us at '}
                  <a href="mailto:contact@redflagai.co" style={{ color: page.altHighlight }}>
                    contact@redflagai.co
                  </a>
                  {' for any changes.'}
                </Text>
              </Box>
            )}
            {planManagementData?.socialPlan?.enabled ? (
              <Box direction="column" gap="0.5rem">
                <Box direction="row" align="center" gap="1rem">
                  <Text size="1.1rem" color={page.primaryText} weight={600}>
                    Social Platform Monitoring
                  </Text>
                  <StyledStatusBadge background="#EDFDF5">
                    <Text size="0.9rem" color="var(--app-success-dark)" weight={600}>Active</Text>
                  </StyledStatusBadge>
                </Box>
                <Box direction="column" gap="0.35rem" pad={{ left: '0.25rem' }}>
                  {currentPlatforms.map((p) => (
                    <Box key={p} direction="row" gap="0.5rem" align="center">
                      <FormNext size="1rem" color="var(--page-alt-highlight)" />
                      <Text size="1rem" color={page.primaryText} weight={500}>
                        {platformLabel(p)}
                      </Text>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : !cidPlan && (
              <Text size="1rem" color={page.primaryText}>No active plan found.</Text>
            )}
          </Box>
        </SectionContainer>

        {/* Upgrade options */}
        {renderUpgradeSection()}
      </Box>
      {/* Signature modal */}
      {showSignModal && (
        <Layer
          onEsc={handleCloseSignModal}
          onClickOutside={handleCloseSignModal}
          modal
        >
          <Box
            width={small ? '100vw' : '900px'}
            pad="1.75rem"
            gap="1.25rem"
            background={page.modalBg}
            overflow={{ vertical: 'auto' }}
          >
            <Box direction="row" justify="between" align="center">
              <Text weight={600} size="1.2rem" color={page.modalText}>
                Review &amp; Sign Agreement
              </Text>
              <Box
                onClick={handleCloseSignModal}
                style={{ cursor: 'pointer', lineHeight: 0 }}
              >
                <FormClose size="1.5rem" color={page.modalText} />
              </Box>
            </Box>

            {/* Agreement acknowledgment */}
            <Box
              gap="0.75rem"
              background={button.selectBg}
              round="0.5rem"
              pad="1rem"
              border={{ color: button.primaryBg, size: '1px' }}
            >
              <Text size="1rem" color={page.modalText} style={{ lineHeight: '1.6' }}>
                {'By signing below, I acknowledge that I have reviewed and agree to '}
                <Text
                  color={button.primaryBg}
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => setShowAgreement(true)}
                >
                  Redflag&apos;s Content Protection Services Agreement
                </Text>
                {', '}
                <Text
                  color={button.primaryBg}
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => setShowTerms(true)}
                >
                  Terms &amp; Conditions
                </Text>
                {', and '}
                <Text
                  color={button.primaryBg}
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => setShowPrivacy(true)}
                >
                  Privacy Policy
                </Text>
                , as updated by this plan upgrade.
              </Text>
            </Box>

            {/* Signature section */}
            <Box gap="0.75rem">
              <Text weight={600} size="1rem" color={page.modalText}>Electronic Signature</Text>

              {/* Existing signature view */}
              {existingSignature && !useNewSignature ? (
                <Box gap="0.75rem">
                  <Text size="0.9rem" color={page.modalText}>
                    Your signature from your original agreement is shown below.
                    You can use it for this upgrade or provide a new one.
                  </Text>
                  <Box
                    background="#f5f5f5"
                    round="var(--app-radius)"
                    pad="0.5rem"
                    border={{ color: button.primaryBg, size: '1px' }}
                    align="center"
                    justify="center"
                    height={`${SIG_HEIGHT}px`}
                  >
                    <img
                      src={existingSignature}
                      alt="Your existing signature"
                      style={{ maxHeight: SIG_HEIGHT - 16, maxWidth: '100%', objectFit: 'contain' }}
                    />
                  </Box>
                  <Box direction="row" gap="small">
                    <AppButton
                      overrideHover
                      onClick={() => setUseNewSignature(true)}
                      level="tableControls"
                      bgColor={page.cardBg}
                      color={button.primaryBg}
                      label="Add a new signature"
                    />
                  </Box>
                </Box>
              ) : (
                /* New signature input */
                <Box gap="0.75rem">
                  {existingSignature && (
                    <AppButton
                      overrideHover
                      onClick={() => { setUseNewSignature(false); handleResetSignature(); }}
                      level="tableControls"
                      bgColor={page.cardBg}
                      color={button.primaryBg}
                      label="← Use existing signature"
                      alignSelf="start"
                    />
                  )}
                  <Box width="fit-content">
                    <RadioButtonSlider
                      radioOptions={[
                        { label: 'Draw', value: 'draw' },
                        { label: 'Type', value: 'type' },
                        { label: 'Upload', value: 'upload' },
                      ]}
                      textColor={page.primaryText}
                      componentBg={page.cardBg}
                      activeButtonBg={button.primaryBg}
                      updateParent={setSigMode}
                      value={sigMode}
                    />
                  </Box>

                  {sigMode === 'draw' && (
                    <div ref={sigContainerRef} style={{ width: '100%' }}>
                      <SignatureCanvas
                        ref={sigPadRef}
                        penColor="#000000"
                        canvasProps={{
                          width: canvasWidth,
                          height: SIG_HEIGHT,
                          style: {
                            display: 'block',
                            width: '100%',
                            background: '#fff',
                            borderRadius: 'var(--app-radius)',
                            border: `1px solid ${button.primaryBg}`,
                            touchAction: 'none',
                          },
                          onMouseUp: handleDrawEnd,
                          onTouchEnd: handleDrawEnd,
                        }}
                        onEnd={handleDrawEnd}
                        clearOnResize={false}
                      />
                    </div>
                  )}

                  {sigMode === 'type' && (
                    <Box
                      background="#fff"
                      round="var(--app-radius)"
                      pad="medium"
                      align="center"
                      justify="center"
                      height={`${SIG_HEIGHT}px`}
                      border={{ color: button.primaryBg, size: '1px' }}
                    >
                      <input
                        type="text"
                        value={typedSignature}
                        onChange={(e) => setTypedSignature(e.target.value)}
                        placeholder="Type your full name"
                        style={{
                          fontSize: '1.35rem',
                          fontWeight: 600,
                          border: 'none',
                          outline: 'none',
                          width: '100%',
                          textAlign: 'center',
                          background: 'transparent',
                          color: '#000000',
                          fontFamily: HANDWRITTEN_FONTS.join(','),
                        }}
                      />
                    </Box>
                  )}

                  {sigMode === 'upload' && (
                    <Box
                      background="#fff"
                      round="var(--app-radius)"
                      pad="medium"
                      border={{ color: button.primaryBg, size: '1px' }}
                      align="center"
                      justify="center"
                      height={`${SIG_HEIGHT}px`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => document.getElementById('upgrade-sig-upload')?.click()}
                    >
                      <input
                        key={fileInputKey}
                        id="upgrade-sig-upload"
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files && e.target.files[0];
                          if (!file) return;
                          const reader = new window.FileReader();
                          reader.onload = (ev) => setMediaSignature(ev.target.result);
                          reader.readAsDataURL(file);
                        }}
                      />
                      <Text size="1rem" color={page.modalText}>
                        {mediaSignature ? 'Image selected' : 'Click to upload signature image'}
                      </Text>
                    </Box>
                  )}

                  <Box direction="row" gap="small">
                    <AppButton
                      overrideHover
                      onClick={handleResetSignature}
                      level="tableControls"
                      bgColor={page.cardBg}
                      color={page.modalText}
                      label="Reset"
                    />
                  </Box>
                </Box>
              )}
            </Box>

            <Text size="0.8rem" color={page.modalText}>
              Your electronic signature is legally binding and timestamped upon submission.
            </Text>

            {/* Actions */}
            <Box direction="row" gap="small" justify="end">
              <AppButton
                overrideHover
                onClick={handleCloseSignModal}
                level="tableControls"
                bgColor={page.cardBg}
                color={page.modalText}
                label="Cancel"
              />
              <AppButton
                overrideHover
                onClick={handleSignAndSubmit}
                level="tableControls"
                disabled={
                  (!useNewSignature && existingSignature)
                    ? false
                    : !getSignatureBase64()
                }
                bgColor={button.primaryBg}
                color="white"
                label="Sign &amp; Confirm"
              />
            </Box>
          </Box>

          {showAgreement && (
            <CidOnboardingTermsModal
              toggle={() => setShowAgreement(false)}
              isSmall={small}
              currentYear={new Date().getFullYear()}
              background={page.modalBg}
              textColor={page.modalText}
            />
          )}
          {showTerms && (
            <TermsOfUseModal
              toggle={() => setShowTerms(false)}
              isSmall={small}
              currentYear={new Date().getFullYear()}
              background={page.modalBg}
              textColor={page.modalText}
            />
          )}
          {showPrivacy && (
            <PrivacyPolicyModal
              toggle={() => setShowPrivacy(false)}
              isSmall={small}
              currentYear={new Date().getFullYear()}
              background={page.modalBg}
              textColor={page.modalText}
            />
          )}
        </Layer>
      )}
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    planManagementData: state.payment.planManagementData,
    planManagementLoading: state.payment.planManagementLoading,
    planUpgradeResponse: state.payment.planUpgradeResponse,
    submitLoading: state.fetchLoader.submissionLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchPlan: fetchPlanManagement,
    clearPlan: clearPlanManagement,
    clearUpgradeResponse: clearPlanUpgradeResponse,
    upgradeSocialPlan: initiateUpgradeSocialPlanRequest,
  }, dispatch);
}

PlanUpgradePage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  planManagementData: PropTypes.shape({
    cidPlan: PropTypes.objectOf(PropTypes.any),
    socialPlan: PropTypes.shape({
      enabled: PropTypes.bool.isRequired,
      platforms: PropTypes.arrayOf(PropTypes.string).isRequired,
      perPlatformMonthlyFeeAmount: PropTypes.number,
      currentMonthlyFeeAmount: PropTypes.number,
    }),
    availableActions: PropTypes.shape({
      canAddSocial: PropTypes.bool,
      canExpandSocial: PropTypes.bool,
      canModifyCid: PropTypes.bool,
    }),
    onboardingId: PropTypes.string,
    existingSignature: PropTypes.string,
    billing: PropTypes.shape({
      billingTimingMode: PropTypes.string,
      billingEffectiveAt: PropTypes.string,
      paymentPending: PropTypes.bool,
      paymentLink: PropTypes.string,
    }),
  }),
  planManagementLoading: PropTypes.bool.isRequired,
  planUpgradeResponse: PropTypes.shape({
    billingTimingMode: PropTypes.string,
    effectiveAt: PropTypes.string,
    billingEffectiveAt: PropTypes.string,
    contractUrl: PropTypes.string,
  }),
  submitLoading: PropTypes.bool.isRequired,
  fetchPlan: PropTypes.func.isRequired,
  clearPlan: PropTypes.func.isRequired,
  clearUpgradeResponse: PropTypes.func.isRequired,
  upgradeSocialPlan: PropTypes.func.isRequired,
};

PlanUpgradePage.defaultProps = {
  planManagementData: null,
  planUpgradeResponse: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(PlanUpgradePage));
